/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { api } from "../lib/api";
import { t } from "../lib/i18n";
import { ProgressRing } from "../components/ProgressRing";
import { BadgesRow } from "../components/Badges";
import { GoalsCard } from "../components/Goals";
import { ChallengeCard } from "../components/Challenge";
import { MorningBrief } from "../components/MorningBrief";
import { FollowupCard } from "../components/FollowupCard";
import { FruitsCard } from "../components/FruitsCard";
import { JeuAidantCard } from "../components/JeuAidantCard";
import { NotebookPen, Users, Siren } from "lucide-react";
import {
  Activity, Pill, HeartPulse, SmilePlus, BookOpen, Brain, Flame, Award, ShieldCheck, ChevronRight,
} from "lucide-react";

const STATUS = { todo: 0, done: 1, partial: 0.5 };

function TaskRow({ icon: Icon, label, status, onClick, testid }) {
  const done = status >= 1;
  const partial = status > 0 && status < 1;
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-full bg-white rounded-2xl border border-[#E4E0D5] px-4 py-3 flex items-center gap-3 text-left hover:border-[#2BA3A0] transition-colors"
    >
      <span
        className={`grid place-items-center w-12 h-12 rounded-xl ${
          done ? "bg-[#5CB85C]/15 text-[#3a8a3a]" : partial ? "bg-[#D4AF5A]/20 text-[#8A6D2B]" : "bg-[#F5F3EE] text-[#2BA3A0]"
        }`}
      >
        <Icon size={24} />
      </span>
      <div className="flex-1">
        <div className="text-[15px] font-bold text-[#0A1426]">{label}</div>
        <div className="text-[12px] text-[#0A1426]/60">
          {done ? "Fait aujourd'hui" : partial ? "En cours" : "À faire"}
        </div>
      </div>
      <ChevronRight size={20} className="text-[#0A1426]/40" />
    </button>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { lang, patient, aidantMode } = useApp();
  const [summary, setSummary] = useState(null);
  const [unseenAlerts, setUnseenAlerts] = useState(0);

  useEffect(() => {
    if (!patient) return;
    api.get(`/dashboard/today?patient_id=${patient.id}`)
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary(null));
    const loadUnseen = () => api.get(`/patients/${patient.id}/critical-alerts/unseen-count`).then(({ data }) => setUnseenAlerts(data.count)).catch(() => null);
    loadUnseen();
    window.addEventListener("bc-alerts-seen", loadUnseen);
    return () => window.removeEventListener("bc-alerts-seen", loadUnseen);
  }, [patient]);

  const taskStatuses = summary
    ? [
        summary.exercice_fait ? STATUS.done : STATUS.todo,
        summary.medicaments_total === 0
          ? STATUS.done
          : summary.medicaments_pris >= summary.medicaments_total
          ? STATUS.done
          : summary.medicaments_pris > 0
          ? STATUS.partial
          : STATUS.todo,
        summary.tension_fait ? STATUS.done : STATUS.todo,
        summary.humeur_fait ? STATUS.done : STATUS.todo,
      ]
    : [0, 0, 0, 0];
  const completed = taskStatuses.reduce((a, b) => a + b, 0);
  const ratio = taskStatuses.length ? completed / taskStatuses.length : 0;

  return (
    <div className="bc-in pt-2 pb-6 flex flex-col gap-5">
      <MorningBrief />
      {aidantMode && (
        <button data-testid="worry-btn" onClick={() => navigate("/app/urgence")}
          className="w-full h-16 rounded-3xl bg-[#D9534F] text-white font-[Fraunces] text-xl font-bold flex items-center justify-center gap-3 shadow-lg shadow-[#D9534F]/30 active:scale-[0.98] transition-transform">
          <Siren size={26} className="text-[#D4AF5A]" /> Je suis inquiet
        </button>
      )}
      {aidantMode && <FollowupCard />}
      {aidantMode && <JeuAidantCard />}
      <FruitsCard />
      {aidantMode && (
        <button data-testid="tile-alertes" onClick={() => navigate("/app/alertes")}
          className={`w-full text-left rounded-3xl p-4 flex items-center gap-3 border ${unseenAlerts ? "bg-[#D9534F] text-white border-[#D9534F]" : "bg-white text-[#0A1426] border-[#E4E0D5]"}`}>
          <span className={`relative w-12 h-12 rounded-2xl grid place-items-center shrink-0 ${unseenAlerts ? "bg-white/15" : "bg-[#D9534F]/10 text-[#D9534F]"}`}>
            <Siren size={24} />
            {unseenAlerts > 0 && <span data-testid="alerts-badge" className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1 rounded-full bg-[#D4AF5A] text-[#0A1426] text-[11px] font-bold grid place-items-center">{unseenAlerts}</span>}
          </span>
          <div className="flex-1">
            <div className="font-[Fraunces] text-base font-bold leading-tight">Alertes urgentes & gestes à faire</div>
            <div className={`text-[11px] leading-snug ${unseenAlerts ? "text-white/80" : "text-[#0A1426]/60"}`}>{unseenAlerts ? `${unseenAlerts} nouvelle(s) alerte(s) tension / glycémie` : "Numéros à appeler, que faire en cas de malaise"}</div>
          </div>
          <ChevronRight size={20} className="opacity-60" />
        </button>
      )}
      {aidantMode && (
        <div className="grid grid-cols-2 gap-3">
          <button data-testid="tile-carnet-aidant" onClick={() => navigate("/app/carnet")}
            className="text-left rounded-3xl p-4 bg-[#0A1426] text-white flex flex-col gap-2">
            <span className="w-11 h-11 rounded-2xl bg-white/15 grid place-items-center"><NotebookPen size={22} className="text-[#D4AF5A]" /></span>
            <div className="font-[Fraunces] text-base font-bold leading-tight">Carnet de l'aidant</div>
            <div className="text-[11px] text-white/75 leading-snug">Vos observations du jour</div>
          </button>
          <button data-testid="tile-famille" onClick={() => navigate("/app/famille")}
            className="text-left rounded-3xl p-4 bg-[#2BA3A0] text-white flex flex-col gap-2">
            <span className="w-11 h-11 rounded-2xl bg-white/15 grid place-items-center"><Users size={22} className="text-white" /></span>
            <div className="font-[Fraunces] text-base font-bold leading-tight">Tableau famille</div>
            <div className="text-[11px] text-white/80 leading-snug">La semaine en un coup d'œil, à partager</div>
          </button>
        </div>
      )}
      {/* Gamification header */}
      <div className="bg-white rounded-3xl border border-[#E4E0D5] p-5 flex items-center gap-4 shadow-sm">
        <ProgressRing value={ratio} size={120} stroke={11}>
          <div className="text-[28px] font-[Fraunces] font-bold text-[#0A1426] leading-none">
            {Math.round(ratio * 100)}%
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[#0A1426]/60 mt-1">aujourd'hui</div>
        </ProgressRing>
        <div className="flex-1">
          <div className="text-[12px] uppercase tracking-wider text-[#0A1426]/60 font-semibold">
            {t(lang, "program_today")}
          </div>
          <div className="font-[Fraunces] text-[20px] font-bold text-[#0A1426] leading-tight mt-0.5">
            {aidantMode ? "Aidons ensemble" : "Continuons doucement"}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <div className="text-center">
              <Award size={18} className="mx-auto text-[#D4AF5A]" />
              <div className="text-[15px] font-bold text-[#0A1426]">{patient?.niveau ?? 1}</div>
              <div className="text-[9px] text-[#0A1426]/60">{t(lang, "level")}</div>
            </div>
            <div className="text-center">
              <Activity size={18} className="mx-auto text-[#2BA3A0]" />
              <div className="text-[15px] font-bold text-[#0A1426]">{patient?.points ?? 0}</div>
              <div className="text-[9px] text-[#0A1426]/60">{t(lang, "points")}</div>
            </div>
            <div className="text-center">
              <Flame size={18} className="mx-auto text-[#D9534F]" />
              <div className="text-[15px] font-bold text-[#0A1426]">{patient?.serie_jours ?? 0}</div>
              <div className="text-[9px] text-[#0A1426]/60">{t(lang, "streak")}</div>
            </div>
          </div>
        </div>
      </div>

      <GoalsCard />
      <ChallengeCard />
      <BadgesRow />

      {/* Tasks */}
      <div className="flex flex-col gap-2.5">
        <TaskRow
          testid="task-exercise"
          icon={Activity}
          label={t(lang, "task_exercise")}
          status={taskStatuses[0]}
          onClick={() => navigate("/app/exercises")}
        />
        <TaskRow
          testid="task-meds"
          icon={Pill}
          label={t(lang, "task_meds")}
          status={taskStatuses[1]}
          onClick={() => navigate("/app/health", { state: { tab: "meds" } })}
        />
        <TaskRow
          testid="task-bp"
          icon={HeartPulse}
          label={t(lang, "task_bp")}
          status={taskStatuses[2]}
          onClick={() => navigate("/app/health", { state: { tab: "bp" } })}
        />
        <TaskRow
          testid="task-mood"
          icon={SmilePlus}
          label={t(lang, "task_mood")}
          status={taskStatuses[3]}
          onClick={() => navigate("/app/health", { state: { tab: "mood" } })}
        />
      </div>

      {/* Quick access */}
      <div>
        <h3 className="text-[12px] uppercase tracking-wider text-[#0A1426]/60 font-bold mb-2">
          {t(lang, "quick_access")}
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <button
            data-testid="tile-word-book"
            onClick={() => navigate("/app/communicate", { state: { tab: "words" } })}
            className="bg-white rounded-2xl border border-[#E4E0D5] p-4 flex flex-col gap-2 items-start hover:border-[#2BA3A0] transition-colors"
          >
            <BookOpen size={28} className="text-[#2BA3A0]" />
            <div className="text-[14px] font-bold text-[#0A1426]">{t(lang, "word_book")}</div>
            <div className="text-[11px] text-[#0A1426]/60 leading-snug">Aphasie · pictogrammes parlants</div>
          </button>
          <button
            data-testid="tile-avc-explained"
            onClick={() => navigate("/app/avc")}
            className="bg-white rounded-2xl border border-[#E4E0D5] p-4 flex flex-col gap-2 items-start hover:border-[#2BA3A0] transition-colors"
          >
            <Brain size={28} className="text-[#2BA3A0]" />
            <div className="text-[14px] font-bold text-[#0A1426]">{t(lang, "avc_explained")}</div>
            <div className="text-[11px] text-[#0A1426]/60 leading-snug">Comprendre simplement</div>
          </button>
        </div>
      </div>

      {/* Prevention card */}
      <div
        data-testid="prevention-card"
        className="rounded-2xl p-5 text-white bg-[#0A1426] relative overflow-hidden"
      >
        <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-[#2BA3A0]/30" />
        <div className="absolute -right-12 top-12 w-20 h-20 rounded-full bg-[#D4AF5A]/25" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/15 text-[10px] uppercase tracking-wider font-bold">
            <ShieldCheck size={12} /> Prévention
          </div>
          <h3 className="font-[Fraunces] text-[22px] leading-tight font-bold mt-3">
            {t(lang, "prevention_title")}
          </h3>
          <p className="text-[13px] text-white/85 mt-2 leading-snug">
            {t(lang, "prevention_body")}
          </p>
        </div>
      </div>
    </div>
  );
}
